import { ArrowRight, CalendarDays, ChevronDown, MapPin, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pickLocalized } from './localized-text';
import { eventTime, fullDate, shortDate } from './branding-event-date';
import { BrandingWordmark, StandardBrandingWordmark, studioPaletteStyle, type StudioEventsViewProps } from './organization-branding-view';
import { EditorialEventsView } from './editorial-events-view';
import { FocusEventsView } from './focus-events-view';
import { BrandingHeroImage } from './branding-hero-image';
export { BrandingWordmark, StandardBrandingWordmark, STUDIO_PALETTES, studioPaletteStyle, type StudioEventsViewProps } from './organization-branding-view';
import heroArtwork from '../../assets/branding/studio-hero-v1.webp';
import './studio-events.css';
import './standard-branding.css';

const COPY = {
  cs: {
    events: 'Akce', enrollments: 'Moje účast', tasks: 'Úkoly',
    headline: ['Vaše příští', 'setkání'], eyebrow: 'Lidé / nápady / lepší zítřky',
    intro: 'Prostor pro inspiraci, sdílení zkušeností a nová spojení. Objevte, co nás čeká.',
    program: 'Zobrazit program', next: 'Další akce', all: 'Všechny akce',
    empty: 'Nová setkání se připravují', emptyBody: 'Jakmile organizace zveřejní akci, najdete ji zde.',
    dateMissing: 'Termín připravujeme', locationMissing: 'Místo bude upřesněno',
    profile: 'Uživatelský účet', contact: 'Kontakt', website: 'Web organizace',
    attendee: 'Pro účastníky', workspaces: 'Pracovní prostory', language: 'Jazyk',
  },
  en: {
    events: 'Events', enrollments: 'My attendance', tasks: 'Tasks',
    headline: ['Your next', 'gathering'], eyebrow: 'People / ideas / a better tomorrow',
    intro: 'A place for inspiration, shared experience and new connections. Discover what is coming next.',
    program: 'View programme', next: 'More events', all: 'All events',
    empty: 'New gatherings are coming', emptyBody: 'Events will appear here when your organization publishes them.',
    dateMissing: 'Date to be confirmed', locationMissing: 'Venue to be confirmed',
    profile: 'User account', contact: 'Contact', website: 'Organization website',
    attendee: 'Attendee', workspaces: 'Workspaces', language: 'Language',
  },
} as const;

export function StudioEventsView({ branding, events, locale, preview = false, logoUrl, heroUrl, includeHeader = preview }: StudioEventsViewProps) {
  if (branding.templateId === 'editorial') return <EditorialEventsView branding={branding} events={events} locale={locale} preview={preview} logoUrl={logoUrl} heroUrl={heroUrl} includeHeader={includeHeader} />;
  if (branding.templateId === 'focus') return <FocusEventsView branding={branding} events={events} locale={locale} preview={preview} logoUrl={logoUrl} heroUrl={heroUrl} includeHeader={includeHeader} />;
  const copy = COPY[locale];
  const ordered = [...events].sort((a, b) => eventTime(a) - eventTime(b) || a.code.localeCompare(b.code));
  const first = ordered[0];
  const rest = ordered.slice(1);
  const studio = branding.templateId === 'studio';
  return (
    <div className={`studio-view${studio ? '' : ' studio-view--standard'}`} style={studioPaletteStyle(branding.paletteId)}>
      {includeHeader && (studio ? (
        <header className="studio-header studio-header--preview">
          <div className="studio-wordmark">
            <BrandingWordmark branding={branding} logoUrl={logoUrl} />
          </div>
          <nav aria-label={copy.events} className="studio-links">
            <button disabled type="button">{copy.events}</button>
            <button disabled type="button">{copy.enrollments}</button>
            <button disabled type="button">{copy.tasks}</button>
          </nav>
          <span className="studio-preview-locale">{locale.toUpperCase()} <ChevronDown size={14} aria-hidden="true" /></span>
          <span className="studio-avatar" aria-label={copy.profile}><UserRound size={18} aria-hidden="true" /></span>
        </header>
      ) : (
        <header className="app-header app-header--workspace standard-header standard-header--preview">
          <div className="app-brand"><StandardBrandingWordmark branding={branding} logoUrl={logoUrl} /></div>
          <nav className="app-nav standard-preview-nav" aria-label={copy.workspaces}>
            <button disabled type="button" className="app-nav__link"><CalendarDays size={18} aria-hidden="true" />{copy.attendee}</button>
          </nav>
          <span className="standard-preview-locale">{copy.language}: {locale.toUpperCase()}</span>
        </header>
      ))}
      <section className="studio-hero">
        <div className="studio-hero__copy">
          <p className="studio-eyebrow">{pickLocalized(branding.tagline, locale) || copy.eyebrow}</p>
          <h1>{first ? <>{copy.headline[0]}<br />{copy.headline[1]}</> : copy.empty}</h1>
          <p className="studio-intro">{pickLocalized(branding.intro, locale) || copy.intro}</p>
          {first ? (
            <div className="studio-hero__event">
              <p className="studio-hero__event-title">{pickLocalized(first.titles, locale)}</p>
              <div className="studio-event-meta">
                <span><CalendarDays size={21} aria-hidden="true" />{fullDate(first, locale) || copy.dateMissing}</span>
                <span><MapPin size={21} aria-hidden="true" />{first.locationName || copy.locationMissing}</span>
              </div>
              {preview ? <button className="studio-cta" disabled type="button">{copy.program}<ArrowRight size={22} aria-hidden="true" /></button> : (
                <Link className="studio-cta" to={`/attendee/events/${first.id}`}>{copy.program}<ArrowRight size={22} aria-hidden="true" /></Link>
              )}
            </div>
          ) : <p className="studio-empty">{copy.emptyBody}</p>}
        </div>
        {(studio || heroUrl) && <div className="studio-hero__art"><BrandingHeroImage overrideUrl={heroUrl} defaultUrl={studio ? heroArtwork : undefined} alt={branding.heroAlt} /></div>}
      </section>
      {rest.length > 0 && (
        <section className="studio-more" id={preview ? undefined : 'more-events'} aria-label={copy.next}>
          <div className="studio-section-heading"><h2>{copy.next}</h2><span aria-hidden="true" />{!preview && <a href="#event-list">{copy.all}<ArrowRight size={18} aria-hidden="true" /></a>}</div>
          <ul className="studio-event-list" id={preview ? undefined : 'event-list'}>
            {rest.map((event) => (
              <li key={event.id}>
                <div className="studio-event-date">{shortDate(event, locale) ? <><strong>{shortDate(event, locale)?.day}{locale === 'cs' ? '.' : ''}</strong><span>{shortDate(event, locale)?.month}{shortDate(event, locale)?.utcFallback && <small> UTC</small>}</span></> : <span>{copy.dateMissing}</span>}</div>
                <div className="studio-event-detail">
                  <h3>{preview ? pickLocalized(event.titles, locale) : <Link to={`/attendee/events/${event.id}`}>{pickLocalized(event.titles, locale)}</Link>}</h3>
                  <p><MapPin size={17} aria-hidden="true" />{event.locationName || copy.locationMissing}</p>
                  {!event.startsAt && <p>{copy.dateMissing}</p>}
                </div>
                {!preview && <Link className="studio-event-arrow" to={`/attendee/events/${event.id}`} aria-label={`${copy.program}: ${pickLocalized(event.titles, locale)}`}><ArrowRight size={24} aria-hidden="true" /></Link>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {(branding.website || branding.contactEmail) && <footer className="studio-contact">
        {branding.website && (preview ? <span>{copy.website}: {branding.website}</span> : <a href={branding.website} target="_blank" rel="noopener noreferrer">{copy.website}<ArrowRight size={16} aria-hidden="true" /></a>)}
        {branding.contactEmail && (preview ? <span>{copy.contact}: {branding.contactEmail}</span> : <a href={`mailto:${encodeURIComponent(branding.contactEmail)}`}>{copy.contact}: {branding.contactEmail}</a>)}
      </footer>}
    </div>
  );
}
